/**
 * The wire protocol between a browser and the zone server (docs/ONLINE.md). Messages are JSON text
 * for now; everything goes through `encodeMessage` and the two decoders, so a binary codec can
 * replace JSON later without touching either side's logic.
 *
 * Up: `hello`, then `cmd` (a `PlayerCommand` with a sequence number) and `ping`.
 * Down: `welcome`, then `snap` (an entity delta plus the tick's `WorldEvent`s), `pong` and `error`.
 *
 * Decoders never throw: anything malformed, oversized or not allowed decodes to `null`, and the
 * caller drops it (the server counts it against the sender).
 */
import { parseAppearance, type Appearance } from '../data/appearance';
import type { WeatherState } from '../data/weather';
import type { ZoneId } from '../data/zones';
import type { PlayerCommand, WorldEvent } from '../world';
import type { NetPatch } from './snapshot';

/** Bumped on any incompatible change; a client with another version is turned away. */
export const PROTOCOL_VERSION = 1;

/** Largest message a client may send (bytes of UTF-8 text). Commands are tiny; this is generous. */
export const MAX_CLIENT_MESSAGE = 4096;

/** The simulation rate (the World's fixed step) and the default snapshot rate. */
export const SIM_HZ = 60;
export const SNAPSHOT_HZ = 20;

// ───────────────────────── messages ─────────────────────────

export type ClientMessage =
  /**
   * First message on a connection. `token` is the session token once accounts exist (an OAuth
   * ticket); `char` is a development character key (a tab's own, honoured only by a dev server);
   * `look` is the character's appearance (the creator's), shown to everyone (clamped on arrival).
   * `name`: the name a guest of the open trial typed (cleaned and made unique by the server, apps/server/src/trial.ts).
   */
  | { k: 'hello'; v: number; token?: string; char?: string; look?: Appearance; name?: string }
  /** A command, numbered 1, 2, 3 … per connection; the server acknowledges the last one applied. */
  | { k: 'cmd'; seq: number; cmd: PlayerCommand }
  /** Round-trip probe; `t` is the client's own clock, echoed back. */
  | { k: 'ping'; t: number };

export type ServerMessage =
  /** Accepted: which entity is yours, where, and the server's clock. */
  | {
      k: 'welcome';
      v: number;
      /** Your character's entity id on the server (`null`: watching only, the slot is taken). */
      you: number | null;
      zone: ZoneId;
      seed: number;
      tick: number;
      hz: number;
      snapHz: number;
    }
  /**
   * One snapshot: the entities in your area of interest that changed since the last snapshot sent
   * to you (whole for ones new to you, only the changed fields for the rest), the ids that left it,
   * and every event since the last snapshot.
   */
  | {
      k: 'snap';
      tick: number;
      /** The last command sequence number the server has applied for you (for reconciliation). */
      ack: number;
      /** The World's clock and hour of the day. */
      time: number;
      hour: number;
      /** The zone's weather (data/weather.ts); left out by older servers. */
      weather?: WeatherState;
      ents: NetPatch[];
      gone: number[];
      events: WorldEvent[];
    }
  | { k: 'pong'; t: number; tick: number }
  /** Your character's progress as the server holds it (a `serializeProgress` save), sent on joining and when it changes. */
  | { k: 'progress'; save: string }
  /** Something was refused. `fatal`: the server closes the connection after this. */
  | { k: 'error'; code: NetErrorCode; text: string; fatal?: boolean };

export type NetErrorCode = 'version' | 'malformed' | 'refused' | 'rate' | 'spectator' | 'full';

// ───────────────────────── commands ─────────────────────────

/**
 * Every `PlayerCommand` type and whether a client may send it over the network. The record is
 * typed against the union, so a new command does not compile until it is listed here.
 * `gm` is never accepted from the network: GM tools on the server will be an operator API.
 */
export const NET_COMMANDS: { readonly [T in PlayerCommand['t']]: boolean } = {
  moveTo: true,
  moveDir: true,
  engage: true,
  targetNearest: true,
  attack: true,
  interact: true,
  select: true,
  untarget: true,
  breakthroughUse: true,
  fastTravel: true,
  partyInvite: true,
  partyKick: true,
  partyLeave: true,
  skill: true,
  block: true,
  sprint: true,
  dash: true,
  jump: true,
  meditate: true,
  sheathe: true,
  pk: true,
  fly: true,
  travel: true,
  mallBuy: true,
  topUpClaim: true,
  passBuy: true,
  passClaim: true,
  passClaimAll: true,
  buy: true,
  sell: true,
  setMount: true,
  setTitle: true,
  mount: true,
  questAccept: true,
  questTurnIn: true,
  trainSkill: true,
  trainNeigong: true,
  trainStop: true,
  arenaFight: true,
  arenaRefresh: true,
  duelChallenge: true,
  duelAnswer: true,
  say: true,
  // the hub rival's best-of-three (systems/duelMatch.ts): only the single-player World's primary acts on it
  duelMatch: true,
  // the offline weekly tournament (systems/tournament.ts): only the single-player World's primary acts on it
  tourney: true,
  // the persistent lock-on (systems/duelLock.ts): the server holds a player's lock
  lockOn: true,
  guildFound: true,
  guildJoin: true,
  guildInvite: true,
  guildLeave: true,
  guildKick: true,
  guildRank: true,
  guildNotice: true,
  guildDonate: true,
  guildChat: true,
  guildDisband: true,
  guildEnemyAdd: true,
  guildEnemyRemove: true,
  guildBannerRaise: true,
  whDeposit: true,
  whWithdraw: true,
  whSplit: true,
  whTidy: true,
  whGold: true,
  whClose: true,
  guildWarSignup: true,
  activityMode: true,
  guildWarClock: false,
  guildWarEnter: true,
  guildWarLeave: true,
  ladderJoin: true,
  ladderCancel: true,
  ladderClaim: true,
  ladderBuy: true,
  trainAuto: true,
  setNeigong: true,
  setArt: true,
  setSkin: true,
  claimBadge: true,
  wearUniform: true,
  joinSchool: true,
  learnArt: true,
  qinggongLearn: true,
  qinggongTrain: true,
  learnNeigong: true,
  equip: true,
  unequip: true,
  emote: true,
  emoji: true,
  forge: true,
  infuse: true,
  gm: false,
  auto: true,
  gather: true,
  water: true,
  fertilize: true,
  buyTool: true,
  plant: true,
  craft: true,
  workStop: true,
  useGood: true,
  buyGood: true,
  sellGood: true,
  danceStart: true,
  danceJoin: true,
  danceLeave: true,
  danceInput: true,
  carriageBoard: true,
  carriageSkip: true,
  walkMode: true,
  slide: true,
  trialStart: true,
  trialQuit: true,
  friendAdd: true,
  friendAccept: true,
  friendDecline: true,
  friendRemove: true,
  enemyAdd: true,
  enemyRemove: true,
  wardrobeWear: true,
  wardrobeShow: true,
  wardrobeDye: true,
  wardrobeSaveSet: true,
  wardrobeLoadSet: true,
  wardrobeRenameSet: true,
  wardrobeClearSet: true,
  npcGift: true,
  npcGiftItem: true,
  offlineJob: true,
  blowPowder: true,
  sackPickup: true,
  sackDrop: true,
  sackSell: true,
  blackBuy: true,
  barSet: true,
  barSwap: true,
  learnMove: false,
  bagMove: true,
  dropItem: true,
  dropGood: true,
  pickup: true,
  sectAct: true,
  sectBind: true,
  sectRead: true,
  fishCast: true,
  fishStrike: true,
  chopStart: true,
  chopHit: true,
  throwHidden: true,
  itemReroll: true,
  itemHone: true,
  itemLock: true,
  itemSocket: true,
  itemUnsocket: true,
  itemDrill: true,
  gemMerge: true,
  itemSalvage: true,
  itemRaise: true,
  redeemToken: true,
  enter: true,
  exit: true,
  changeFloor: true,
  navTo: true,
  navStop: true,
  autoQuest: true,
  autoGather: true,
  sceneAdvance: true,
  sceneSkip: true,
  sceneReplay: true,
  weather: false,
};

/** Longest string a command field may hold (ids, item keys). */
const MAX_FIELD = 64;
/** Largest coordinate or amount a command field may hold. */
const MAX_NUMBER = 1e6;

/** A field value a command may carry: a bounded number or string, a flag, null, or a point. */
function fieldOk(v: unknown, depth: number): boolean {
  if (v === null || typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v) && Math.abs(v) <= MAX_NUMBER;
  if (typeof v === 'string') return v.length <= MAX_FIELD;
  if (depth > 0 || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  return keys.length <= 4 && keys.every((key) => fieldOk(o[key], depth + 1));
}

/**
 * The shape check a server makes before a command reaches the World: a known type a client may
 * send, a handful of flat fields of plain values (one level of nesting, for a ground target).
 * The World then applies its own rules (range, cooldown, cost), as it does offline.
 */
export function isNetCommand(v: unknown): v is PlayerCommand {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const t = o.t;
  if (typeof t !== 'string' || !Object.hasOwn(NET_COMMANDS, t)) return false;
  if (!NET_COMMANDS[t as PlayerCommand['t']]) return false;
  const keys = Object.keys(o);
  return keys.length <= 6 && keys.every((key) => key === 't' || fieldOk(o[key], 0));
}

// ───────────────────────── encode / decode ─────────────────────────

export function encodeMessage(m: ClientMessage | ServerMessage): string {
  return JSON.stringify(m);
}

function parse(raw: string, max: number): Record<string, unknown> | null {
  if (raw.length > max) return null;
  try {
    const v: unknown = JSON.parse(raw);
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

const isSeq = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) > 0;

/** What the server accepts from a client: one of the three messages, well formed, or null. */
export function decodeClientMessage(raw: string): ClientMessage | null {
  const o = parse(raw, MAX_CLIENT_MESSAGE);
  if (!o) return null;
  switch (o.k) {
    case 'hello':
      if (!Number.isSafeInteger(o.v)) return null;
      if (o.token !== undefined && (typeof o.token !== 'string' || o.token.length > 512)) return null;
      if (o.char !== undefined && (typeof o.char !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(o.char))) return null;
      if (o.look !== undefined && (typeof o.look !== 'object' || o.look === null || Array.isArray(o.look)))
        return null;
      if (o.name !== undefined && (typeof o.name !== 'string' || o.name.length > 64)) return null;
      return {
        k: 'hello',
        v: o.v as number,
        ...(o.token === undefined ? {} : { token: o.token as string }),
        ...(o.char === undefined ? {} : { char: o.char as string }),
        // Any appearance is taken, repaired: unknown ids become the defaults, the body is clamped.
        ...(o.look === undefined ? {} : { look: parseAppearance(o.look) }),
        ...(o.name === undefined ? {} : { name: o.name as string }),
      };
    case 'cmd':
      return isSeq(o.seq) && isNetCommand(o.cmd) ? { k: 'cmd', seq: o.seq, cmd: o.cmd } : null;
    case 'ping':
      return typeof o.t === 'number' && Number.isFinite(o.t) ? { k: 'ping', t: o.t } : null;
    default:
      return null;
  }
}

/**
 * What a client accepts from the server. The server is trusted, so this checks only the envelope
 * (the kind and its fields' types), not every event.
 */
export function decodeServerMessage(raw: string): ServerMessage | null {
  const o = parse(raw, Infinity);
  if (!o) return null;
  const num = (k: string) => typeof o[k] === 'number';
  switch (o.k) {
    case 'welcome':
      return num('v') && num('tick') && num('hz') && num('snapHz') && typeof o.zone === 'string'
        ? (o as ServerMessage)
        : null;
    case 'snap':
      return num('tick') &&
        num('ack') &&
        num('time') &&
        num('hour') &&
        Array.isArray(o.ents) &&
        Array.isArray(o.gone) &&
        Array.isArray(o.events)
        ? (o as ServerMessage)
        : null;
    case 'pong':
      return num('t') && num('tick') ? (o as ServerMessage) : null;
    case 'progress':
      return typeof o.save === 'string' ? (o as ServerMessage) : null;
    case 'error':
      return typeof o.code === 'string' && typeof o.text === 'string' ? (o as ServerMessage) : null;
    default:
      return null;
  }
}
